'use client';
import React, { Fragment, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { API_URL } from '../constants';
import { apiRequest } from '../utils/api';

const Login: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();
    const { publicKey, connected } = useWallet();

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home');
        }
    }, [isAuthenticated, navigate]);

    // Handle wallet login when wallet connects
    useEffect(() => {
        const handleWalletLogin = async () => {
            if (connected && publicKey && !isAuthenticated) {
                setLoading(true);
                setError('');

                try {
                    // Note: Login doesn't use auth headers, so use fetch directly
                    const response = await fetch(API_URL + '/api/auth/login', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            walletAddress: publicKey.toString()
                        }),
                    });

                    const data = await response.json();

                    if (response.ok) {
                        login(data.token, data.user);
                        navigate('/home');
                    } else {
                        // If user doesn't exist, register them
                        if (response.status === 401) {
                            await handleWalletRegister();
                        } else {
                            setError(data.error || 'Wallet login failed');
                        }
                    }
                } catch (error) {
                    setError('Network error. Please try again.');
                } finally {
                    setLoading(false);
                }
            }
        };

        handleWalletLogin();
    }, [connected, publicKey, isAuthenticated]);

    const handleWalletRegister = async () => {
        if (!publicKey) return;

        try {
            const response = await fetch(API_URL + '/api/auth/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    walletAddress: publicKey.toString(),
                    role: 'consumer'
                }),
            });

            const data = await response.json();

            if (response.ok) {
                login(data.token, data.user);
                navigate('/home');
            } else {
                setError(data.error || 'Registration failed');
            }
        } catch (error) {
            setError('Network error. Please try again.');
        }
    };


    return (
        <Fragment>
            <div className="main-wrap">
                <div className="nav-header bg-transparent shadow-none border-0">
                    <div className="nav-top w-100">
                        {/* <Link to="/login"><i className="feather-zap text-success display1-size me-2 ms-0"></i><span className="d-inline-block fredoka-font ls-3 fw-600 text-current font-xxl logo-text mb-0">Solyfans </span> </Link> */}
                        <button className="nav-menu me-0 ms-auto"></button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-5 d-none d-xl-block p-0 vh-100 bg-image-cover bg-no-repeat"
                        style={{ backgroundImage: `url("assets/images/login.svg")` }}></div>
                    <div className="col-xl-7 vh-100 align-items-center d-flex bg-white rounded-3 overflow-hidden">
                        <div className="card shadow-none border-0 ms-auto me-auto login-card">
                            <div className="card-body rounded-0 text-left">
                                <h2 className="fw-700 display1-size display2-md-size mb-3">Login into <br />your account</h2>

                                {error && (
                                    <div className="alert alert-danger font-xsss fw-500 mb-3" role="alert">
                                        {error}
                                    </div>
                                )}

                                <div className="text-center">
                                    <div className="mb-3">
                                        <p className="text-grey-600 font-xsss">Connect your Solana wallet to login or create an account</p>
                                        <p className="text-grey-500 font-xsss mt-2">
                                            <i className="feather-info font-xs me-1"></i>
                                            New users get a username from their wallet address
                                        </p>
                                    </div>
                                    <WalletMultiButton className="btn btn-dark fw-600 w-100 p-3" />
                                    {loading && (
                                        <div className="mt-3">
                                            <p className="text-grey-600 font-xsss">Processing wallet login...</p>
                                        </div>
                                    )}
                                    <h6 className="text-grey-500 font-xsss fw-500 mt-3 mb-0 lh-32">Don't have a wallet? <Link to="/register" className="fw-700 ms-1">Get Started</Link></h6>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Fragment>
    )
}

export default Login